/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useMemo } from 'react';

import { cssMap, jsx } from '@compiled/react';
import { useIntl } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import CrossIcon from '@atlaskit/icon/core/close';
import FilterIcon from '@atlaskit/icon/core/filter';
import SearchIcon from '@atlaskit/icon/core/search';
import { Box, Inline, Stack } from '@atlaskit/primitives/compiled';
import Textfield from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';
import { DropdownTrigger } from '@atlassian/entry-point-dropdown';

import { FilterConfig } from '../types';

import { filtersDropdownEntryPoint } from './filters-dropdown/entrypoint';
import { messages } from './messages';
import { SelectedFiltersRow } from './selected-filters-row';

interface SearchAndFilterBarProps {
	filterConfig?: FilterConfig;
	placeholder?: string;
	searchText: string;
	selectedFilters: string[];
	onSearchChange: (searchText: string) => void;
	onFilterChange: (filters: string[]) => void;
	testId?: string;
}

/**
 * Component to render the search input & filters dropdown
 */
export function SearchAndFilterBar({
	filterConfig,
	placeholder,
	searchText,
	selectedFilters,
	onSearchChange,
	onFilterChange,
	testId,
}: SearchAndFilterBarProps) {
	const { formatMessage } = useIntl();
	const showFilters = !!filterConfig?.filters?.length;
	const showSelectedFilters = showFilters && !!selectedFilters?.length;

	const entryPointProps = useMemo(
		() => showFilters && { filterConfig, selectedFilters, onFilterChange },
		[showFilters, filterConfig, selectedFilters, onFilterChange],
	);

	return (
		<Stack xcss={styles.searchBox} space="space.075" testId={testId}>
			{/* Search Input Row */}
			<Inline space="space.100">
				<Textfield
					isCompact
					placeholder={placeholder ?? formatMessage(messages.searchPlaceholder)}
					value={searchText}
					onChange={(e) => onSearchChange(e.currentTarget.value)}
					elemBeforeInput={
						<Box xcss={styles.searchIcon}>
							<SearchIcon label="" />
						</Box>
					}
					elemAfterInput={
						searchText && (
							<Box xcss={styles.searchCancelIcon}>
								<IconButton
									label={formatMessage(messages.clearSearchIconLabel)}
									icon={(iconProps) => <CrossIcon {...iconProps} size="small" />}
									onClick={() => onSearchChange('')}
									spacing="compact"
								/>
							</Box>
						)
					}
				/>
				{/* Filters dropdown */}
				{!!entryPointProps && (
					<DropdownTrigger
						entryPoint={filtersDropdownEntryPoint}
						entryPointProps={entryPointProps}
						trigger={({ triggerRef, ...props }) => (
							<IconButton
								{...props}
								label={formatMessage(messages.filterIconLabel)}
								icon={FilterIcon}
								isDisabled={filterConfig?.isDisabled}
								ref={triggerRef}
							/>
						)}
						placement="right-start"
						strategy="absolute"
					/>
				)}
			</Inline>

			{/* Selected Filters Row */}
			{showSelectedFilters && (
				<SelectedFiltersRow
					filterConfig={filterConfig}
					selectedFilters={selectedFilters}
					onFilterChange={onFilterChange}
				/>
			)}
		</Stack>
	);
}

const styles = cssMap({
	searchBox: {
		paddingInline: token('space.050'),
		paddingBlock: token('space.075'),
	},
	searchIcon: {
		paddingInlineStart: token('space.075'),
		paddingInlineEnd: token('space.025'),
		paddingBlockEnd: token('space.025'),
		display: 'flex',
		alignItems: 'center',
	},
	searchCancelIcon: {
		paddingInlineEnd: token('space.050'),
	},
});
